import { useSearch } from "@tanstack/react-router";

/**
 * Where to send someone once they sign in.
 *
 * Mirrors `routes/search.ts`'s `SectionSearch` — validated on read rather
 * than trusted, since a URL can be typed or truncated by hand.
 */
export interface SignInSearch {
  /** The path that redirected here. Absent means "just go to the dashboard". */
  redirect?: string;
}

/**
 * Only an internal path is accepted. `redirect` becomes an argument to
 * `navigate({ to })`, so anything else (a bare protocol-relative URL, say)
 * is a link this app has no route for, not a place to send someone.
 */
export function validateSignInSearch(
  search: Record<string, unknown>,
): SignInSearch {
  return typeof search.redirect === "string" && search.redirect.startsWith("/")
    ? { redirect: search.redirect }
    : {};
}

/** The current route's sign-in search params, typed and validated. */
export function useSignInSearch(): SignInSearch {
  const raw: unknown = useSearch({ strict: false });
  return validateSignInSearch(
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>)
      : {},
  );
}
